import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Navigate, useOutletContext, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

import type { ChatLayoutContext, ChatMessage, ChatSession } from './ChatMain';

export default function ChatConversation() {
  const { chatId } = useParams<{ chatId: string }>();
  const { chats, sendMessage, loadingChatId } = useOutletContext<ChatLayoutContext>();
  const isLoading = loadingChatId === chatId;

  const [composer, setComposer] = useState('');
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null);

  const activeChat = useMemo(
    () => chats.find((chat: ChatSession) => chat.id === chatId),
    [chats, chatId]
  );

  useEffect(() => {
    const messages = activeChat?.messages;
    if (!messages?.length) return;
    const last = messages[messages.length - 1];
    if (last.role === 'user') {
      bottomAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeChat?.messages.length]);

  if (!chatId || !activeChat) {
    return <Navigate to="/chat/new" replace />;
  }

  const handleSend = () => {
    const trimmed = composer.trim();
    if (!trimmed) return;
    sendMessage(activeChat.id, trimmed);
    setComposer('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className="mx-auto flex h-full w-full max-w-[50rem] flex-col"
    >
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
        className="mb-5 flex items-center gap-2.5 border-b border-slate-100 pb-4 dark:border-white/[0.06]"
      >
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#2B7FFF] shadow-[0_0_10px_rgba(43,127,255,0.4)]">
          <span className="text-[8px] font-bold text-white">B</span>
        </div>
        <h1 className="truncate text-[13px] font-medium text-slate-500 dark:text-white/45">
          {activeChat.title}
        </h1>
      </motion.header>

      {/* Messages */}
      <div className="flex-1 space-y-5 overflow-y-auto pr-0.5">
        <AnimatePresence initial={false}>
          {activeChat.messages.map((message: ChatMessage) => {
            const isUser = message.role === 'user';
            return (
              <motion.div
                key={message.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className={isUser ? 'flex justify-end' : 'flex items-start gap-3'}
              >
                {!isUser && (
                  <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2B7FFF] shadow-[0_0_10px_rgba(43,127,255,0.35)]">
                    <span className="text-[8px] font-bold text-white">B</span>
                  </div>
                )}

                <div
                  className={isUser
                    ? 'max-w-[72%] rounded-2xl rounded-tr-sm bg-[#2B7FFF] px-4 py-3 text-[13.5px] leading-relaxed text-white shadow-[0_2px_10px_rgba(43,127,255,0.28)]'
                    : 'min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-slate-100/80 bg-slate-50/80 px-5 py-4 text-[13.5px] text-slate-800 dark:border-white/[0.06] dark:bg-white/[0.04] dark:text-white/85'}
                >
                  {isUser ? (
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  ) : (
                    <div className="prose prose-sm max-w-none dark:prose-invert
                      prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-slate-900 dark:prose-headings:text-white/90
                      prose-h3:text-[14px] prose-h3:mt-4 prose-h3:mb-1.5
                      prose-p:text-slate-700 prose-p:my-1.5 prose-p:leading-relaxed dark:prose-p:text-white/70
                      prose-ul:my-1.5 prose-ul:pl-4
                      prose-li:my-0.5 prose-li:text-slate-700 dark:prose-li:text-white/70
                      prose-strong:font-semibold prose-strong:text-slate-900 dark:prose-strong:text-white/90
                      prose-a:text-[#2B7FFF] prose-a:no-underline hover:prose-a:underline
                      prose-code:text-[#2B7FFF] prose-code:bg-[#EEF4FF] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[12px] dark:prose-code:bg-[#2B7FFF]/10
                      prose-table:text-xs prose-th:bg-slate-100 prose-th:px-3 prose-th:py-2 dark:prose-th:bg-white/[0.06]
                      prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-slate-200 dark:prose-td:border-white/[0.07]
                      prose-th:border prose-th:border-slate-200 dark:prose-th:border-white/[0.07]">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Typing indicator */}
        {isLoading && (
          <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-3"
          >
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2B7FFF] shadow-[0_0_10px_rgba(43,127,255,0.35)]">
              <span className="text-[8px] font-bold text-white">B</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm border border-slate-100/80 bg-slate-50/80 px-5 py-4 dark:border-white/[0.06] dark:bg-white/[0.04]">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{ animationDelay: `${i * 0.18}s` }}
                  className="typing-dot h-[5px] w-[5px] rounded-full bg-slate-400 dark:bg-white/30"
                />
              ))}
            </div>
          </motion.div>
        )}

        <div ref={bottomAnchorRef} />
      </div>

      {/* Composer */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, delay: 0.05, ease: 'easeOut' }}
        className="mt-4"
      >
        <div className="rounded-2xl border border-slate-200/90 bg-white shadow-[0_2px_16px_rgba(15,23,42,0.07)] transition-shadow focus-within:shadow-[0_4px_24px_rgba(15,23,42,0.1),0_0_0_1px_rgba(43,127,255,0.12)] dark:border-white/[0.07] dark:bg-[rgba(10,16,36,0.8)] dark:shadow-[0_2px_20px_rgba(0,0,0,0.4)] dark:backdrop-blur-2xl dark:focus-within:border-[#2B7FFF]/25">
          <textarea
            rows={2}
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply..."
            className="w-full resize-none bg-transparent px-5 pb-2 pt-4 text-[13.5px] leading-relaxed text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white/90 dark:placeholder:text-white/20"
          />
          <div className="flex items-center justify-between px-4 pb-3.5 pt-1">
            <span className="text-[11px] font-medium text-slate-400/70 dark:text-white/18">
              ⏎ send &nbsp;·&nbsp; ⇧⏎ new line
            </span>
            <button
              type="button"
              onClick={handleSend}
              disabled={!composer.trim() || isLoading}
              className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-[#2B7FFF] text-white shadow-[0_1px_8px_rgba(43,127,255,0.45)] transition-all hover:bg-[#1A6AE8] hover:shadow-[0_2px_14px_rgba(43,127,255,0.55)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-25 disabled:shadow-none"
            >
              <ArrowUp className="h-[15px] w-[15px]" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.section>
  );
}
